import Footer from "@/components/layout/Footer";
import Header from "@/components/layout/Header";
import WelcomeGarizimRiveSud from "@/components/layout/WelcomeGarizimRiveSud";
import { Button } from "@/components/ui/button";
import {
  Calendar,
  Clock,
  ExternalLink,
  MapPin,
  Sparkles,
  Users,
} from "lucide-react";
import { Helmet } from "react-helmet";

const PageIndexGarizimRiveSud = () => {
  const canonicalUrl =
    typeof window !== "undefined" ? window.location.href : "";

  return (
    <div className="min-h-screen bg-white font-sans text-slate-900">
      <Helmet>
        <title>
          Église Garizim - Campus Rive-Sud (Longueuil) | Cultes & Rassemblements
        </title>
        <meta
          name="description"
          content="Bienvenue à l'Église de Dieu Garizim Rive-Sud. Retrouvez notre assemblée chaque dimanche à 14h00 au 530 Rue Saint-Jean, Longueuil pour un culte d'adoration et de louange."
        />
        <link rel="canonical" href={canonicalUrl} />
        <meta
          property="og:title"
          content="Église Garizim - Campus Rive-Sud (Longueuil)"
        />
        <meta
          property="og:description"
          content="Retrouvez notre assemblée chaque dimanche à 14h00 au 530 Rue Saint-Jean, Longueuil."
        />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
      </Helmet>

      <Header />

      <main>
        {/* Hero Section */}
        <WelcomeGarizimRiveSud />

        {/* Pastoral Vision Section */}
        <section className="py-10 sm:py-20 lg:py-28 bg-slate-50 border-b border-slate-200/80">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-12 lg:gap-16 items-center">
              {/* Image Column (Cols 1 to 6) */}
              <div className="lg:col-span-6">
                <div className="relative group">
                  <div className="aspect-[4/3] sm:aspect-[16/11] rounded-3xl overflow-hidden bg-slate-200 border border-slate-200/90 shadow-xl shadow-slate-200/80">
                    <img
                      src="https://ik.imagekit.io/4apzi8lz0/egarizim/generational-pastors.jpg?updatedAt=1760896561625"
                      alt="Pasteur Serge Joseph et direction pastorale"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="absolute -bottom-5 -right-5 bg-white rounded-2xl p-4 border border-slate-200 shadow-xl hidden sm:flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-700">
                      <Sparkles className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                        Garizim Rive-Sud
                      </p>
                      <p className="text-xs text-slate-500">
                        Un nouveau chapitre de foi
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Text Column (Cols 7 to 12) */}
              <div className="lg:col-span-6 space-y-4 sm:space-y-6 text-left">
                <div className="inline-flex items-center gap-2 px-3 py-1 sm:px-3.5 sm:py-1.5 rounded-full bg-white border border-slate-200 text-slate-700 text-[10px] sm:text-xs font-bold tracking-widest uppercase shadow-xs">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                  <span>Vision Pastorale & Expansion</span>
                </div>

                <div className="space-y-0.5 sm:space-y-1">
                  <h2 className="text-2xl sm:text-5xl font-black text-slate-900 tracking-tight leading-tight">
                    Pasteur Serge Joseph
                  </h2>
                  <p className="text-amber-800 font-bold text-xs sm:text-sm tracking-wider uppercase">
                    Fondateur & Conducteur Spirituel
                  </p>
                </div>

                <div className="space-y-3 sm:space-y-4 text-slate-600 text-xs sm:text-base lg:text-lg leading-relaxed">
                  <p>
                    Pasteur Serge Joseph poursuit son œuvre d'édification avec
                    la fondation de <strong>Garizim Rive-Sud</strong>,
                    élargissant ainsi la portée et le rayonnement de la mission
                    pastorale.
                  </p>
                  <p className="hidden sm:block">
                    Ce nouveau chapitre témoigne d’une détermination profonde à
                    répondre aux besoins spirituels, fraternels et sociaux des
                    familles chrétiennes de la Rive-Sud de Montréal, tout en
                    demeurant fidèle aux principes bibliques, à l'excellence et
                    à l'amour fraternel qui caractérisent son ministère.
                  </p>
                </div>

                <div className="pt-1 sm:pt-2 flex flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm text-slate-600">
                  <div className="flex items-center gap-1.5 sm:gap-2 px-3 py-1.5 sm:px-3.5 sm:py-2 rounded-xl bg-white border border-slate-200 shadow-xs">
                    <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-600" />
                    <span>Communauté inclusive</span>
                  </div>
                  <div className="flex items-center gap-1.5 sm:gap-2 px-3 py-1.5 sm:px-3.5 sm:py-2 rounded-xl bg-white border border-slate-200 shadow-xs">
                    <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-sky-600" />
                    <span>Rassemblement dominical</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Gathering & Location Section */}
        <section
          id="services-rive-sud"
          className="py-10 sm:py-20 lg:py-28 bg-white"
        >
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="max-w-3xl mx-auto text-center mb-8 sm:mb-16 space-y-2 sm:space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 sm:px-3.5 sm:py-1.5 rounded-full bg-slate-100 border border-slate-200 text-slate-800 text-[10px] sm:text-xs font-bold tracking-widest uppercase shadow-xs">
                <Clock className="w-3.5 h-3.5 text-amber-600" />
                <span>Culte Dominical & Accès</span>
              </div>
              <h2 className="text-2xl sm:text-5xl font-black text-slate-900 tracking-tight">
                Sanctuaire de la Rive-Sud
              </h2>
              <p className="text-slate-600 text-xs sm:text-lg leading-relaxed">
                À l’église « Groupe Biblique de la Rive-Sud », au 530 Rue
                Saint-Jean, Longueuil.
              </p>
            </div>

            {/* Content & Map Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 items-center max-w-7xl mx-auto">
              {/* Left Details Card (Cols 1 to 6) */}
              <div className="lg:col-span-6 space-y-4 sm:space-y-6">
                <div className="bg-slate-50 rounded-3xl p-5 sm:p-8 border border-slate-200/90 shadow-sm space-y-4 sm:space-y-6 text-left">
                  {/* Schedule Block */}
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-amber-100/80 border border-amber-200/80 flex items-center justify-center shrink-0 text-amber-800">
                      <Clock className="w-5 h-5 sm:w-6 sm:h-6" />
                    </div>
                    <div>
                      <span className="text-[10px] sm:text-xs font-bold text-amber-800 uppercase tracking-wider block">
                        Horaire
                      </span>
                      <h3 className="text-lg sm:text-2xl font-black text-slate-900 tracking-tight">
                        Dimanche à 14h00
                      </h3>
                      <p className="text-slate-500 text-xs sm:text-sm mt-0.5">
                        Culte d'adoration, louange et enseignement de la Parole.
                      </p>
                    </div>
                  </div>

                  {/* Address Block */}
                  <div className="flex items-start gap-3 sm:gap-4 pt-3 sm:pt-4 border-t border-slate-200/80">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-slate-200/70 border border-slate-300/70 flex items-center justify-center shrink-0 text-slate-700">
                      <MapPin className="w-5 h-5 sm:w-6 sm:h-6" />
                    </div>
                    <div>
                      <span className="text-[10px] sm:text-xs font-bold text-slate-500 uppercase tracking-wider block">
                        Adresse
                      </span>
                      <h3 className="text-base sm:text-lg font-bold text-slate-900">
                        Groupe Biblique de la Rive-Sud
                      </h3>
                      <p className="text-slate-600 text-xs sm:text-sm mt-0.5">
                        530 Rue Saint-Jean, Longueuil, QC J4H 2Y4
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="pt-1 sm:pt-2">
                    <Button
                      size="lg"
                      className="w-full bg-slate-900 hover:bg-slate-800 text-white font-bold py-2.5 sm:py-3.5 rounded-2xl text-xs sm:text-base shadow-xl shadow-slate-900/10 transition-all flex items-center justify-center gap-2"
                      onClick={() =>
                        window.open(
                          "https://share.google/4Xg2v1gYUpSKD8KiU",
                          "_blank",
                        )
                      }
                    >
                      <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-400" />
                      <span>Ouvrir dans Google Maps</span>
                      <ExternalLink className="w-3.5 h-3.5 sm:w-4 sm:h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Right Map Image Card (Cols 7 to 12) */}
              <div className="lg:col-span-6">
                <a
                  href="https://share.google/4Xg2v1gYUpSKD8KiU"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block group rounded-3xl overflow-hidden border border-slate-200 shadow-xl bg-slate-100 relative hover:shadow-2xl transition-all duration-300"
                >
                  <img
                    src="https://ik.imagekit.io/4apzi8lz0/egarizim/530-maps.jpg?updatedAt=1760896561563"
                    alt="Plan d'accès 530 Rue Saint-Jean, Longueuil"
                    className="w-full h-64 sm:h-96 object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent flex items-end p-4 sm:p-6">
                    <div className="flex items-center justify-between w-full text-white">
                      <div>
                        <p className="text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-amber-300">
                          Plan de localisation
                        </p>
                        <p className="text-xs sm:text-sm font-bold">
                          530 Rue Saint-Jean, Longueuil
                        </p>
                      </div>
                      <span className="inline-flex items-center gap-1 text-[11px] sm:text-xs font-semibold bg-white/20 hover:bg-white/30 backdrop-blur-md px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full transition-colors">
                        <span>Agrandir</span>
                        <ExternalLink className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                      </span>
                    </div>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PageIndexGarizimRiveSud;
